import React, { useEffect, useState, useRef } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { NewsItem, getNewsItems, createNewsItem, updateNewsItem, deleteNewsItem } from '../../services/newsService';
import { uploadFileToS3 } from '../../services/s3Upload';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AdminNewsManager: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const { isDark } = useAdminTheme();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    imageUrl: '',
    linkUrl: '',
    author: '',
    tags: [] as string[],
    isRichText: false,
    type: 'NEW RELEASE' as NewsItem['type'],
    order: 0,
    isActive: true,
  });
  const [tagInput, setTagInput] = useState('');
  const [useRichText, setUseRichText] = useState(false);

  const isFormValid = !!form.title.trim() && !!form.description.trim();

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getNewsItems();
      setNewsItems(data);
    } catch (e) {
      console.error(e);
      showError('Failed to load news items');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ 
      title: '', 
      description: '', 
      imageUrl: '',
      linkUrl: '',
      author: '',
      tags: [],
      isRichText: false,
      type: 'NEW RELEASE', 
      order: newsItems.length, 
      isActive: true 
    });
    setUseRichText(false);
    setTagInput('');
    setShowModal(true);
  };

  const openEdit = (item: NewsItem) => {
    setEditing(item);
    setForm({ 
      title: item.title, 
      description: item.description, 
      imageUrl: item.imageUrl || '',
      linkUrl: item.linkUrl || '',
      author: item.author || '',
      tags: item.tags || [],
      isRichText: item.isRichText || false,
      type: item.type, 
      order: item.order || 0, 
      isActive: item.isActive !== false 
    });
    setUseRichText(item.isRichText || false);
    setTagInput('');
    setShowModal(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setForm({ ...form, tags: form.tags.filter(tag => tag !== tagToRemove) });
  };

  const submit = async () => {
    try {
      if (!form.title.trim() || !form.description.trim()) {
        showError('Please fill title and description');
        return;
      }
      if (isUploadingImage) {
        showError('Please wait for the image upload to finish');
        return;
      }
      setIsSubmitting(true);
      const submitData = {
        ...form,
        isRichText: useRichText,
      };
      if (editing) {
        await updateNewsItem(editing.id, submitData);
        showSuccess('News item updated');
      } else {
        await createNewsItem(submitData as any);
        showSuccess('News item created');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      console.error(e);
      showError('Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this news item?')) return;
    try {
      await deleteNewsItem(id);
      showSuccess('Deleted');
      await load();
    } catch (e) {
      console.error(e);
      showError('Failed to delete');
    }
  };

  const toggleActive = async (item: NewsItem) => {
    try {
      await updateNewsItem(item.id, { isActive: !item.isActive });
      await load();
    } catch (e) {
      showError('Failed to update status');
    }
  };

  const getTypeColor = (type: NewsItem['type']) => {
    switch (type) {
      case 'NEW RELEASE':
        return 'bg-green-500 text-white';
      case 'UPDATE':
        return 'bg-blue-500 text-white';
      case 'FEATURE':
        return 'bg-purple-500 text-white';
      case 'ANNOUNCEMENT':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">News Center</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Manage news items shown in the "Follow updates and releases" section on the Discover page</p>
        </div>
        <button onClick={openAdd} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg">Add News</button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">Loading...</div>
      ) : (
        <div className="space-y-4">
          {newsItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No news items found. Create your first news item to get started.
            </div>
          ) : (
            newsItems.map(item => (
              <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                        {item.type}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${item.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {item.isRichText ? (
                        <div 
                          className="line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      ) : (
                        <p className="line-clamp-2">{item.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {item.author && <span>Author: {item.author}</span>}
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.tags.map((tag, idx) => (
                            <span key={idx} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>Order: {item.order || 0}</span>
                      <span>Created: {item.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                      {item.updatedAt && (
                        <span>Updated: {item.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button 
                      onClick={() => openEdit(item)} 
                      className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => toggleActive(item)} 
                      className={`px-3 py-2 text-sm rounded transition-colors ${
                        item.isActive 
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800' 
                          : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                      }`}
                    >
                      {item.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      onClick={() => remove(item.id)} 
                      className="px-3 py-2 text-sm bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editing ? 'Edit News Item' : 'Add News Item'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title *
                </label>
                <input 
                  value={form.title} 
                  onChange={e => setForm({ ...form, title: e.target.value })} 
                  placeholder="e.g., Work with a faster, modern booking calendar..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description *
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <input 
                      type="checkbox" 
                      checked={useRichText} 
                      onChange={e => setUseRichText(e.target.checked)} 
                      className="rounded"
                    /> 
                    Use Rich Text Editor
                  </label>
                </div>
                {useRichText ? (
                  <div className={`border border-gray-300 dark:border-gray-600 rounded overflow-hidden ${isDark ? 'quill-dark' : ''}`}>
                    <style>{`
                      .quill-dark .ql-container {
                        background-color: #374151;
                        color: #f3f4f6;
                      }
                      .quill-dark .ql-editor {
                        color: #f3f4f6;
                      }
                      .quill-dark .ql-toolbar {
                        background-color: #1f2937;
                        border-bottom: 1px solid #4b5563;
                      }
                      .quill-dark .ql-stroke {
                        stroke: #9ca3af;
                      }
                      .quill-dark .ql-fill {
                        fill: #9ca3af;
                      }
                      .quill-dark .ql-picker-label {
                        color: #9ca3af;
                      }
                      .quill-dark .ql-picker-options {
                        background-color: #1f2937;
                        border: 1px solid #4b5563;
                      }
                    `}</style>
                    <ReactQuill
                      theme="snow"
                      value={form.description}
                      onChange={(value) => setForm({ ...form, description: value })}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'color': [] }, { 'background': [] }],
                          ['link', 'image'],
                          ['clean']
                        ]
                      }}
                      formats={[
                        'header',
                        'bold', 'italic', 'underline', 'strike',
                        'list', 'bullet',
                        'color', 'background',
                        'link', 'image'
                      ]}
                      style={{ minHeight: '200px' }}
                    />
                  </div>
                ) : (
                  <textarea 
                    value={form.description} 
                    onChange={e => setForm({ ...form, description: e.target.value })} 
                    placeholder="e.g., Give clients a sleek calendar with improved performance..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none" 
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Image URL (optional)
                </label>
                <input 
                  value={form.imageUrl} 
                  onChange={e => setForm({ ...form, imageUrl: e.target.value })} 
                  placeholder="https://images.unsplash.com/photo-..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                />
                <div className="mt-2 flex items-center gap-2">
                  <label className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded cursor-pointer">
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={async (e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file) return;
                        
                        if (!file.type.startsWith('image/')) {
                          showError('Please select an image file');
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          return;
                        }
                        
                        if (file.size > 10 * 1024 * 1024) {
                          showError('Image size must be less than 10MB');
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          return;
                        }
                        
                        try {
                          setIsUploadingImage(true);
                          setUploadProgress(0);
                          console.log('Starting image upload...', { fileName: file.name, fileSize: file.size, fileType: file.type });
                          const result = await uploadFileToS3(file, { 
                            makePublic: true, 
                            keyPrefix: 'news',
                            onProgress: (p) => {
                              console.log('Upload progress:', p + '%');
                              setUploadProgress(p);
                            }
                          });
                          console.log('Upload result:', result);
                          
                          if (!result || !result.publicUrl) {
                            throw new Error('Upload succeeded but no public URL returned');
                          }
                          
                          setForm({ ...form, imageUrl: result.publicUrl });
                          console.log('Image URL set to:', result.publicUrl);
                          showSuccess('Image uploaded successfully');
                        } catch (err: any) {
                          console.error('Upload error details:', err);
                          console.error('Error code:', err?.code);
                          console.error('Error message:', err?.message);
                          console.error('Full error:', JSON.stringify(err, null, 2));
                          const errorMessage = err?.message || err?.code || 'Upload failed. Please check your browser console for details.';
                          showError(errorMessage);
                        } finally {
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                          setIsUploadingImage(false);
                          setUploadProgress(null);
                        }
                      }} 
                    />
                    Upload image
                  </label>
                  {isUploadingImage && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      Uploading{typeof uploadProgress === 'number' ? ` ${uploadProgress}%` : '...'}
                    </span>
                  )}
                </div>
                {form.imageUrl && (
                  <div className="mt-2 h-32 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                    <img 
                      src={form.imageUrl} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Author (optional)
                </label>
                <input 
                  value={form.author} 
                  onChange={e => setForm({ ...form, author: e.target.value })} 
                  placeholder="e.g., John Doe"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags (optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input 
                    value={tagInput} 
                    onChange={e => setTagInput(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Type a tag and press Enter"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link URL (optional)
                </label>
                <input 
                  value={form.linkUrl} 
                  onChange={e => setForm({ ...form, linkUrl: e.target.value })} 
                  placeholder="https://example.com/news/article"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Add a link to read more about this news item
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type *
                  </label>
                  <select 
                    value={form.type} 
                    onChange={e => setForm({ ...form, type: e.target.value as NewsItem['type'] })} 
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="NEW RELEASE">NEW RELEASE</option>
                    <option value="UPDATE">UPDATE</option>
                    <option value="FEATURE">FEATURE</option>
                    <option value="ANNOUNCEMENT">ANNOUNCEMENT</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Order
                  </label>
                  <input 
                    type="number" 
                    value={form.order} 
                    onChange={e => setForm({ ...form, order: parseInt(e.target.value || '0', 10) })} 
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input 
                    type="checkbox" 
                    checked={form.isActive} 
                    onChange={e => setForm({ ...form, isActive: e.target.checked })} 
                    className="rounded"
                  /> 
                  Active (visible on Discover page)
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button 
                onClick={() => setShowModal(false)} 
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={submit} 
                disabled={!isFormValid || isSubmitting || isUploadingImage}
                title={!isFormValid ? 'Fill all required fields' : isUploadingImage ? 'Wait for upload to finish' : ''}
                className={`px-4 py-2 rounded transition-colors ${
                  (!isFormValid || isSubmitting || isUploadingImage) 
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
                    : 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200'
                }`}
              >
                {isSubmitting ? (editing ? 'Updating...' : 'Adding...') : (editing ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsManager;

